'use client';
import Accordion from '@repo/ui/src/components/Accordion';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { useCustomer } from '@hooks/customer';
import { NoteProps, useRecipeNotes } from '@hooks/customer/useRecipeNotes';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import { classNames } from '@repo/ui/src/utils/classNames';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useCallback, useState } from 'react';
import NoteForm from './_NoteForm';
import { RecipeNote, SegmentRecipeProps } from './_RecipeNote';

export type ID = string | number;

interface Props {
  id: string;
  segmentRecipe: SegmentRecipeProps;
}

const RecipeNotes = ({ id, segmentRecipe }: Props) => {
  const { t } = useTranslation();
  const { data: user } = useCustomer();
  const { push } = useModal(ModalID.login);
  const { data } = useRecipeNotes({ recipeId: id });
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<NoteProps | null>(null);

  const showEditForm = useCallback((note: NoteProps) => {
    setEditingItem(note);
    setShowForm(true);
  }, []);
  const showAddForm = useCallback(() => {
    setEditingItem(null);
    setShowForm(true);
  }, []);

  return (
    <div className="tw-bg-warm-100 tw-relative tw-p-6 sm:tw-p-20">
      <div className="tw-max-w-body tw-m-auto tw-bg-white tw-grid tw-gap-10 tw-p-6 md:tw-grid-cols-2 md:tw-py-20 md:tw-px-28">
        <div className="tw-max-w-[360px]">
          <h2 className="tra-text-web-heading-2">
            {t('recipeNotesContent.title', undefined, { default: 'My Notes' })}
          </h2>
          <hr className="tw-h-1 tw-w-12 tw-bg-brand-300 tw-block tw-my-6" />
          {!user ? (
            <div>
              <p className="tra-text-web-body-xl">
                {t('recipeNotesContent.unauthedDescription', undefined, {
                  default:
                    'In order to add notes for this recipe, you must log in or create an account.',
                })}
              </p>

              <div className="tw-pt-6 tw-flex tw-flex-col">
                <Button onClick={() => push()}>
                  {t('recipeNotesContent.login', undefined, {
                    default: 'Login',
                  })}
                </Button>
              </div>
            </div>
          ) : (
            <p className="tra-text-web-body-xl">
              {t('recipeNotesContent.authedDescription', undefined, {
                default: 'Add notes during your cook to save for the future.',
              })}
            </p>
          )}
        </div>
        <div className="tw-flex tw-gap-y-6 tw-flex-col">
          <div className="tw-grid tw-grid-areas-overlap">
            <div
              className={classNames(
                showForm ? 'tw-z-10' : 'tw-opacity-0',
                'tw-grid-in-a tw-transition-opacity tw-duration-300',
              )}
            >
              {user && (
                <NoteForm
                  recipeId={id}
                  setShowForm={setShowForm}
                  visible={showForm}
                  note={editingItem}
                  segmentRecipe={segmentRecipe}
                />
              )}
            </div>

            {user && (
              <div
                className={classNames(
                  showForm ? 'tw-opacity-0' : 'tw-z-10',
                  'tw-self-start tw-grid-in-a tw-transition-opacity tw-duration-300 tw-grid tw-gap-y-10',
                )}
              >
                {!data?.length ? (
                  <p>
                    {t('recipeNotesContent.noNoteText', undefined, {
                      default: 'No notes yet, looks like you need to get cooking.',
                    })}
                  </p>
                ) : (
                  <Accordion
                    className="tw-grid tw-gap-y-6"
                    preExpanded={[data?.[0]?.id as ID]}
                    onChange={() => setEditingItem(null)}
                    data-testid="notes-accordion"
                  >
                    {data?.map((note) => (
                      <RecipeNote
                        key={note.id}
                        note={note}
                        recipeId={id}
                        showEditForm={showEditForm}
                        segmentRecipe={segmentRecipe}
                      />
                    ))}
                  </Accordion>
                )}
                <Button variant={ButtonVariant.secondary} onClick={showAddForm}>
                  {t('recipeNotesContent.addButton', undefined, {
                    default: 'Add A Note',
                  })}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeNotes;
